import { useState } from "react";
import { 
  Box, 
  Table, 
  TableBody, 
  TableCell, 
  TableContainer, 
  TableHead, 
  TableRow, 
  Typography,
  Paper,
  Chip,
  TablePagination,
  Button
} from "@mui/material";
import { AddCircle } from "@mui/icons-material";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../store/hooks/useAppSelector";
import { RootState } from "../../store/types/rootState";
import { useGetRafflesQuery } from "../../store/api";
import { useSelector } from "react-redux";

export const RafflesList = () => {
  const accountInformation = useAppSelector(({ account }: RootState) => account);
  const profile = useSelector(({ user }: RootState) => user.profile);
  const navigate = useNavigate();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  const { data: rafflesFounded = [], error: errorGetRaffles } = useGetRafflesQuery(undefined, {
    skip: accountInformation.id !== 'vip-rifas',
  });

  // Filtrar por fecha si el perfil es operator (solo rifas creadas después del 3-11-2025)
  const filterDate = new Date('2025-11-03T00:00:00Z'); // 3 de noviembre de 2025
  
  const filteredRaffles = [...(profile === 'operator'
    ? rafflesFounded.filter(raffle => {
        if (raffle.createdDate) {
          const raffleDate = new Date(raffle.createdDate);
          return raffleDate > filterDate;
        }
        return false; // Si no tiene createdDate, excluirla
      }).filter(raffle => raffle.accountId !== 'oscar-alarcon' && raffle.accountId !== 'agudojhonny' && raffle.accountId !== 'carlos-pinango' && raffle.accountId !== 'gerej-16')
    : rafflesFounded
  )].sort((a, b) => {
    const dateA = a.createdDate ? new Date(a.createdDate).getTime() : 0;
    const dateB = b.createdDate ? new Date(b.createdDate).getTime() : 0;
    return dateB - dateA; // Orden descendente (más reciente primero)
  });
 
  if (errorGetRaffles) {
    return (
      <Box>
        <Typography variant="h6" color="error">
          Error al cargar las rifas
        </Typography>
      </Box>
    );
  }

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  // Calcular rifas paginadas
  const paginatedRaffles = filteredRaffles.slice(
    page * rowsPerPage,
    page * rowsPerPage + rowsPerPage
  );

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3} flexWrap="wrap" gap={2}>
        <Typography variant="h4" color="white" fontWeight="bold">
          Rifas
        </Typography>
        <Button
          variant="contained"
          color="primary"
          startIcon={<AddCircle />}
          onClick={() => navigate('/create')}
          sx={{
            fontWeight: 'bold',
            textTransform: 'none',
            px: 3,
            py: 1.5
          }}
        >
          Crear Rifa Nueva
        </Button>
      </Box>

      <TableContainer component={Paper} sx={{ backgroundColor: '#1a1a1a', color: '#fff' }}>
        <Table>
          <TableHead>
            <TableRow sx={{ backgroundColor: '#2d2d2d' }}>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Fecha</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Nombre</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Cuenta</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Estado</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Tipo</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Precio Ticket</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Precio USD</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Límite</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Lanzamiento</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Premium</TableCell>
              <TableCell sx={{ color: '#fff', fontWeight: 'bold' }}>Métodos de Pago</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredRaffles.length === 0 ? (
              <TableRow>
                <TableCell colSpan={10} align="center" sx={{ color: '#fff', py: 4 }}>
                  No hay rifas disponibles
                </TableCell>
              </TableRow>
            ) : (
              paginatedRaffles.map((raffle) => {
                const launchDateFormatted = raffle.launchDate
                  ? new Date(raffle.launchDate).toLocaleDateString()
                  : "No definida";

                return (
                  <TableRow
                    key={raffle.id}
                    sx={{
                      '&:hover': {
                        backgroundColor: '#2d2d2d'
                      }
                    }}
                  >
                    <TableCell sx={{ color: '#fff' }}>{raffle.createdDate ? new Date(raffle.createdDate).toLocaleDateString() : 'No definida'}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{raffle.name}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{raffle.accountId}</TableCell>
                    <TableCell>
                      <Chip
                        label={raffle.status}
                        size="small"
                        sx={{
                          backgroundColor: raffle.status === 'ACTIVE' ? '#4caf50' : '#f44336',
                          color: '#fff'
                        }}
                      />
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      {raffle.type.toUpperCase()}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      ${raffle.ticketPrice.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      ${raffle.ticketPriceUSD.toFixed(2)}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>{raffle.limit}</TableCell>
                    <TableCell sx={{ color: '#fff' }}>{launchDateFormatted}</TableCell>
                    <TableCell>
                      {raffle.isPremium ? (
                        <Chip label="Premium" size="small" color="primary" />
                      ) : (
                        <Chip label="No" size="small" sx={{ backgroundColor: '#666', color: '#fff' }} />
                      )}
                    </TableCell>
                    <TableCell sx={{ color: '#fff' }}>
                      {raffle.availablePaymentMethods?.join(', ') || 'N/A'}
                    </TableCell>
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
        <TablePagination
          component="div"
          count={filteredRaffles.length}
          page={page}
          onPageChange={handleChangePage}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={handleChangeRowsPerPage}
          rowsPerPageOptions={[5, 10, 25, 50]}
          sx={{
            color: '#fff',
            backgroundColor: '#1a1a1a',
            borderTop: '1px solid #333',
            '& .MuiTablePagination-toolbar': {
              color: '#fff',
            },
            '& .MuiTablePagination-selectLabel': {
              color: '#fff',
            },
            '& .MuiTablePagination-displayedRows': {
              color: '#fff',
            },
            '& .MuiTablePagination-select': {
              color: '#fff',
              '& .MuiOutlinedInput-notchedOutline': {
                borderColor: '#666',
              },
              '&:hover .MuiOutlinedInput-notchedOutline': {
                borderColor: '#fff',
              },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                borderColor: '#fff',
              },
            },
            '& .MuiIconButton-root': {
              color: '#fff',
              '&:disabled': {
                color: '#666',
              },
            },
          }}
        />
      </TableContainer>
    </Box>
  );
};
